/**
 * Cursor and selection.
 *
 * Two distinct things that most implementations conflate: a *cursor* (exactly
 * one row, always present, moved by j/k) and a *selection set* (zero or more
 * rows, toggled by x). The rule that makes the whole keyboard model coherent is
 * "an action applies to the selection if it is non-empty, otherwise to the
 * cursor row" — so the same keystroke works whether or not you have selected
 * anything, and you never have to look to find out what you are about to hit.
 */

/**
 * THE CURSOR IS A ROW ID, NEVER AN INDEX, and that is the one thing a
 * virtualised list cannot paper over.
 *
 * It was `cursor: number` — an index into the loaded page — while both documents
 * that describe this screen said twice that it must be an id
 * (interaction.md:89 "Cursor is stored as a row id, never an index";
 * shell-ia.md:319 "Focus tracked by row id — a message GUID in v1 — re-attached
 * when a recycled row returns"). The gap is not cosmetic. interaction.md:110
 * says a change feed drops vanished ids silently, so the row ABOVE the cursor
 * can disappear between two keystrokes: with an index, the ring stays where it
 * was drawn and the next `#` trashes the row that slid under it. Reproduced on
 * the old code — cursor on `c` in [a,b,c,d], drop `a`, and `resolveTarget`
 * returned `d`. And @tanstack/react-virtual recycles DOM nodes BY INDEX, so the
 * recovery both documents prescribe ("re-attach focus by id") had no id to
 * re-attach to.
 *
 * A vanished cursor id resolves to NO ROW rather than to a neighbour: an action
 * on a row that is gone must do nothing, not something. Movement treats it as
 * "before the list", so one `j` re-attaches at the top.
 */
export interface SelectionState {
  readonly cursorId: string | null
  readonly selected: ReadonlySet<string>
  /** Anchor for shift-range extension. A row id, for the same reason. */
  readonly anchorId: string | null
  /**
   * When true, the user asked for every row matching the query, not just the
   * loaded page. Acting on this requires an explicit confirmation that restates
   * the count, because it can be tens of thousands of messages.
   */
  readonly escalated: boolean
}

export const emptySelection: SelectionState = {
  cursorId: null,
  selected: new Set(),
  anchorId: null,
  escalated: false,
}

export interface Row {
  readonly id: string
}

const clampIndex = (index: number, length: number): number =>
  length === 0 ? 0 : Math.min(Math.max(index, 0), length - 1)

/**
 * Where the cursor sits in THIS page, and the two ways that can fail are not the
 * same failure:
 *
 *   - `cursorId === null` — nobody has moved yet. The cursor is "exactly one
 *     row, always present" (see the header), so it is the first row: a fresh
 *     list where `#` hits the top message is the behaviour this file has always
 *     had, and the focus ring is drawn there.
 *   - the id is set and NOT in `rows` — the row was deleted or filtered out from
 *     under the user. There is no honest answer, so this returns -1 and the
 *     callers diverge: an action targets nothing, and the next `j`/`k`
 *     re-attaches at the top.
 */
export function cursorIndex(state: SelectionState, rows: readonly Row[]): number {
  if (state.cursorId === null) return rows.length === 0 ? -1 : 0
  return rows.findIndex((r) => r.id === state.cursorId)
}

/** The row the cursor is on, or undefined when its id is no longer loaded. */
export function cursorRow(state: SelectionState, rows: readonly Row[]): Row | undefined {
  const at = cursorIndex(state, rows)
  return at < 0 ? undefined : rows[at]
}

export function moveCursor(state: SelectionState, delta: number, rows: readonly Row[]): SelectionState {
  // A vanished cursor is -1, so a move in EITHER direction clamps to the first
  // row: the id it held is gone and there is no index left to be nearly right
  // about. Being nearly right is the bug.
  const next = rows[clampIndex(cursorIndex(state, rows) + delta, rows.length)]
  return { ...state, cursorId: next?.id ?? null }
}

export function setCursor(state: SelectionState, index: number, rows: readonly Row[]): SelectionState {
  const next = rows[clampIndex(index, rows.length)]
  return { ...state, cursorId: next?.id ?? null }
}

export function toggleAtCursor(state: SelectionState, rows: readonly Row[]): SelectionState {
  const row = cursorRow(state, rows)
  if (!row) return state
  const selected = new Set(state.selected)
  if (selected.has(row.id)) selected.delete(row.id)
  else selected.add(row.id)
  // Any manual change drops the escalation: the user is now curating a set by
  // hand, and silently keeping "all 12,438" would be a trap.
  return { ...state, cursorId: row.id, selected, anchorId: row.id, escalated: false }
}

/** Shift+j/k: extend from the anchor to the new cursor, inclusive. */
export function extendTo(state: SelectionState, index: number, rows: readonly Row[]): SelectionState {
  const target = clampIndex(index, rows.length)
  // The anchor is resolved through the CURRENT page too: if the anchor row was
  // dropped, the range starts from where the cursor actually is rather than from
  // a stale offset that now names somebody else.
  const anchorAt = state.anchorId === null ? -1 : rows.findIndex((r) => r.id === state.anchorId)
  const cursorAt = cursorIndex(state, rows)
  const anchor = anchorAt >= 0 ? anchorAt : cursorAt >= 0 ? cursorAt : target
  const [from, to] = anchor <= target ? [anchor, target] : [target, anchor]
  const selected = new Set(state.selected)
  for (let i = from; i <= to; i++) {
    const row = rows[i]
    if (row) selected.add(row.id)
  }
  return {
    ...state,
    cursorId: rows[target]?.id ?? null,
    anchorId: rows[anchor]?.id ?? null,
    selected,
    escalated: false,
  }
}

/** `*a` — select every loaded row. Escalation beyond the page is a second step. */
export function selectAllLoaded(state: SelectionState, rows: readonly Row[]): SelectionState {
  return { ...state, selected: new Set(rows.map((r) => r.id)), escalated: false }
}

export function selectWhere(
  state: SelectionState,
  rows: readonly Row[],
  predicate: (row: Row) => boolean,
): SelectionState {
  return { ...state, selected: new Set(rows.filter(predicate).map((r) => r.id)), escalated: false }
}

export function clearSelection(state: SelectionState): SelectionState {
  return { ...state, selected: new Set(), anchorId: null, escalated: false }
}

/** The explicit second step: "also select the N that match but are not loaded". */
export function escalate(state: SelectionState): SelectionState {
  return { ...state, escalated: true }
}

/**
 * How many rows the query matches, and whether the server actually knows.
 *
 * This is `GET /search`'s `count: {value, exact}` with `value` renamed at the
 * boundary, because in the UI it is the total of a set the user is about to act
 * on. `exact: false` means THE TRUE COUNT IS GREATER THAN `total` — the server
 * capped it at COUNT_CAP rather than finishing the pass — so "more than 10,000"
 * renders from `total` itself and never from `total - 1`.
 *
 * The pair travels together instead of a bare number because every place a count
 * is USED has to make a different decision when it is capped, and a `number`
 * has nowhere to put "capped". That is the whole reason this file moves in S3:
 * the escalated state was written against a `total: number` before there was a
 * count that could be inexact.
 */
export interface MatchCount {
  readonly total: number
  readonly exact: boolean
}

export type Target =
  | { readonly kind: 'rows'; readonly ids: readonly string[] }
  | { readonly kind: 'query'; readonly total: number; readonly exact: boolean }

/**
 * What an action applies to. This single function is why every triage binding
 * behaves the same from the list and from inside a thread.
 *
 * `match` IS REQUIRED, AND IT USED TO DEFAULT to `{total: rows.length, exact:
 * true}`. The comment defending that default said the loaded page is exact by
 * construction — true, and true of the two `rows` arms, which never read
 * `match`. The only arm that reads it is the escalated one, and there the
 * default is a lie with a confirmation attached: `resolveTarget(escalate(s),
 * page)` returned `{kind:'query', total: 50, exact: true}`, and
 * `confirmationFor('trash', …)` then rendered "trash 50 conversations? This
 * affects every message that matches the search, not just the ones on screen" —
 * a Yes-able dialog naming the page size for an action over the whole result
 * set. Rule 14 exists to stop exactly that dialog, and a default made it
 * opt-in. Nothing in `SelectionState` carries a count, so the server's
 * `count: {value, exact}` has to be threaded in by hand; making the parameter
 * required is what turns forgetting it from a wrong number into a type error.
 */
export function resolveTarget(state: SelectionState, rows: readonly Row[], match: MatchCount): Target {
  if (state.escalated) return { kind: 'query', total: match.total, exact: match.exact }
  if (state.selected.size > 0) return { kind: 'rows', ids: [...state.selected] }
  const row = cursorRow(state, rows)
  return { kind: 'rows', ids: row ? [row.id] : [] }
}

/**
 * The `MatchCount` of a page the client is holding, for the two `rows` arms and
 * for a list that has loaded everything the query matches.
 *
 * It exists so that a call site which genuinely means "the loaded page" says so
 * in one word instead of being handed it by a default it did not ask for.
 */
export const loadedPage = (rows: readonly Row[]): MatchCount => ({ total: rows.length, exact: true })

export interface EscalationPrompt {
  readonly loaded: number
  readonly total: number
  /** False when `total` is a floor rather than the count. See `MatchCount`. */
  readonly exact: boolean
  readonly message: string
  readonly offer: string
}

/**
 * The banner shown after `*a` when the query matches more than is loaded.
 *
 * THE "MORE THAN" FORM IS NOT DECORATION. A capped count is a lower bound, and
 * an offer that reads "Select all 10,000 conversations" when the truth is 41,000
 * is a lie the user then acts on. The inexact copy names the bound as a bound.
 *
 * `total <= loaded` suppresses the banner only when the count is EXACT: an
 * inexact `total` is a floor the true count exceeds, so there is always more to
 * select than is on screen, and hiding the offer would strand the user on a page.
 */
export function escalationPrompt(loaded: number, match: MatchCount): EscalationPrompt | null {
  if (match.exact && match.total <= loaded) return null
  return {
    loaded,
    total: match.total,
    exact: match.exact,
    message: `All ${loaded.toLocaleString()} conversations on this page are selected.`,
    offer: match.exact
      ? `Select all ${match.total.toLocaleString()} conversations that match this search.`
      : `Select all conversations that match this search — more than ${match.total.toLocaleString()} conversations.`,
  }
}

export interface Confirmation {
  readonly required: boolean
  /**
   * True when the confirmation CANNOT BE GIVEN as things stand: the dialog has
   * no number to show, so its only honest button is "count them exactly", not
   * "yes". A UI that treats `required` as "show a yes/no dialog" and ignores
   * this would put a Yes button under a sentence that refuses to name a figure.
   */
  readonly blocked: boolean
  readonly message: string
}

const DESTRUCTIVE = new Set(['trash', 'spam', 'delete.forever'])

/** Nothing to confirm: an ordinary selection, or an action that is undoable. */
const NO_CONFIRMATION: Confirmation = { required: false, blocked: false, message: '' }

/**
 * Destructive actions on an escalated set always confirm, restating the count
 * and the action. Non-destructive ones (archive, label) do not — they are
 * undoable, and a confirm dialog on every bulk archive trains people to click
 * through dialogs without reading.
 *
 * RULE 14, AND THIS IS THE CLIENT HALF OF IT. A count with `exact: false` is a
 * floor: the server stopped at COUNT_CAP. "Trash 10,000 conversations?" over a
 * set that is really 41,000 is a dialog that describes a different action from
 * the one the button performs, and the user cannot tell. So an inexact count
 * does not produce a smaller dialog or a hedged one — it produces a REFUSAL that
 * names the round trip that fixes it (`count=exact`, or interaction.md:112-114's
 * ESEARCH COUNT). The message deliberately contains no digits at all: there is
 * no number here anybody should read, and half a number is worse than none.
 */
export function confirmationFor(
  action: string,
  target: Target,
  /**
   * What this build can actually do.
   *
   * The origin grew `POST /ops/query`, so this is true in the client that
   * knows how to call it. It stays an ARGUMENT rather than becoming a constant:
   * the dialog and the capability have to move together, and a test needs to be
   * able to assert both halves without editing the module it is testing.
   */
  options?: { readonly queryWideOps?: boolean },
): Confirmation {
  if (target.kind !== 'query') return NO_CONFIRMATION
  // An undoable action never named a count, so a capped one cannot mislead it.
  if (!DESTRUCTIVE.has(action)) return NO_CONFIRMATION
  if (!target.exact) {
    return {
      required: true,
      blocked: true,
      message: `Hearth will not ${action} a set it has only counted approximately. The match count was capped, not finished. Ask for an exact count (count=exact) and confirm against the real number.`,
    }
  }
  // THE DIALOG DESCRIBED AN ACTION NOTHING PERFORMS, and that is worse than
  // any of the cases above it.
  //
  // The message said "This affects every message that matches the search, not
  // just the ones on screen", and the client then sent ONE OPERATION PER LOADED
  // ROW: a hundred of a matching four thousand. The reader confirmed a
  // whole-query delete, watched a hundred messages go, and had no way to learn
  // that the other 3,900 were untouched — in the one dialog that exists
  // specifically to stop a mistake at scale.
  //
  // ACTING ON THE PAGE AND SAYING SO WOULD NOT BE THE FIX EITHER. The reader
  // asked for every match, by clicking a control that says so; quietly doing a
  // page of them is a different action, not a smaller one.
  //
  // So it refuses, and names what is missing. The origin has no whole-query
  // mutation — `/ops` takes a list of (mailbox, uid) pairs, and building the
  // list means walking the search to its end — so this is a capability that has
  // to be built, not a flag to turn on. `MailSource.search()` is the primitive;
  // docs/next.md carries it.
  if (!options?.queryWideOps) {
    return {
      required: true,
      blocked: true,
      message:
        `Hearth cannot ${action} every message that matches a search yet — it can only act on the ` +
        `conversations it has loaded, and you have asked for all ${target.total.toLocaleString()}. ` +
        `Load more of them and select those, or narrow the search until the matches fit.`,
    }
  }

  return {
    required: true,
    blocked: false,
    message: `${action} ${target.total.toLocaleString()} conversations? This affects every message that matches the search, not just the ones on screen.`,
  }
}
